import { env } from '../env';

/**
 * SSE consumer for the chat endpoint. Wraps fetch + ReadableStream parsing
 * so the UI can subscribe to callbacks without touching framing details.
 *
 * Why fetch (not EventSource)? EventSource doesn't let us set the
 * Authorization header — we need bearer auth on the request, and the
 * standard EventSource API only supports cookies. fetch + manual SSE
 * parsing is the standard workaround.
 *
 * Server protocol (see apps/api/src/routes/ai-chat.ts):
 *   event: session     data: { sessionId, userMessageId }
 *   event: token       data: { text }
 *   event: done        data: { messageId }
 *   event: error       data: { message }
 *
 * The callbacks are best-effort — a missing onSession is fine if the caller
 * already knows the session id.
 */

type GetToken = () => Promise<string | null>;
let getAuthToken: GetToken = async () => null;

// Re-exported from the same affordance api.ts uses; the caller wires once.
export function setChatStreamTokenProvider(fn: GetToken): void {
  getAuthToken = fn;
}

export type StreamHandlers = {
  onSession?: (sessionId: string) => void;
  onToken?: (text: string) => void;
  onDone?: (messageId: string | null) => void;
  onError?: (message: string) => void;
};

export async function streamChatTurn(
  payload: { sessionId: string | null; content: string },
  handlers: StreamHandlers,
): Promise<void> {
  const token = await getAuthToken();
  const res = await fetch(`${env.VITE_API_BASE_URL}/v1/ai/chat/messages`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'text/event-stream',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      ...(payload.sessionId ? { sessionId: payload.sessionId } : {}),
      content: payload.content,
    }),
  });

  if (!res.ok || !res.body) {
    handlers.onError?.(`Chat unavailable (${res.status})`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // Split on the SSE event boundary (\n\n). Anything left after the last
    // boundary is a partial event, stays in buf for the next chunk.
    let boundary = buf.indexOf('\n\n');
    while (boundary !== -1) {
      const frame = buf.slice(0, boundary);
      buf = buf.slice(boundary + 2);
      boundary = buf.indexOf('\n\n');
      handleFrame(frame, handlers);
    }
  }
}

function handleFrame(frame: string, handlers: StreamHandlers): void {
  let eventName = 'message';
  let data = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) eventName = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) data += line.slice('data:'.length).trim();
  }
  if (!data) return;
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(data) as Record<string, unknown>;
  } catch {
    return;
  }
  switch (eventName) {
    case 'session':
      if (typeof parsed.sessionId === 'string') handlers.onSession?.(parsed.sessionId);
      break;
    case 'token':
      if (typeof parsed.text === 'string') handlers.onToken?.(parsed.text);
      break;
    case 'done':
      handlers.onDone?.(typeof parsed.messageId === 'string' ? parsed.messageId : null);
      break;
    case 'error':
      handlers.onError?.(
        typeof parsed.message === 'string' ? parsed.message : 'The assistant failed.',
      );
      break;
  }
}
